import { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { useSystemDate } from '../hooks/useSystemDate';
import { AccountingEngine } from '../services/accountingEngine';

export const PayrollProcessingView = ({ handleItemSelect }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const systemDate = useSystemDate();
  const parsedDate = new Date(systemDate);
  
  const [selectedMonth, setSelectedMonth] = useState(parsedDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(parsedDate.getFullYear());
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingLog, setProcessingLog] = useState<string[]>([]);

  const employees = useLiveQuery(() => 
    activeCompany ? db.employees.where('companyId').equals(activeCompany.id!).and(e => e.status === 'Active').toArray() : []
  , [activeCompany]);

  const existingPayroll = useLiveQuery(() => 
    activeCompany ? db.payroll.where('companyId').equals(activeCompany.id!).and(p => p.month === selectedMonth && p.year === selectedYear).toArray() : []
  , [activeCompany, selectedMonth, selectedYear]);

  const isAlreadyProcessed = existingPayroll && existingPayroll.length > 0 && existingPayroll.some(p => p.status === 'Processed');

  const handleProcessPayroll = async () => {
    if (!activeCompany || !employees || employees.length === 0) {
      alert('No active employees found to process payroll.');
      return;
    }

    if (isAlreadyProcessed) {
      alert('Payroll for this month is already processed and locked.');
      return;
    }

    if (!confirm(`Process payroll for ${employees.length} employees for ${selectedMonth}/${selectedYear}?`)) return;

    setIsProcessing(true);
    setProcessingLog(['Starting payroll processing...']);

    try {
      // 1. Create Journal Entry for Salary Expense
      // We need to find or create ledgers for Salary Expense and Salary Payable
      let salaryExpenseLedger = await db.ledgers.where('companyId').equals(activeCompany.id!).and(l => l.name === 'Salary Expense').first();
      if (!salaryExpenseLedger) {
        const expenseGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Indirect Expenses').first();
        const id = await db.ledgers.add({
          companyId: activeCompany.id!,
          name: 'Salary Expense',
          groupId: expenseGroup?.id || null,
          openingBalance: 0,
          balanceType: 'Dr',
          isDefault: false
        });
        salaryExpenseLedger = await db.ledgers.get(id as number);
      }

      let salaryPayableLedger = await db.ledgers.where('companyId').equals(activeCompany.id!).and(l => l.name === 'Salary Payable').first();
      if (!salaryPayableLedger) {
        const liabilityGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Current Liabilities').first();
        const id = await db.ledgers.add({
          companyId: activeCompany.id!,
          name: 'Salary Payable',
          groupId: liabilityGroup?.id || null,
          openingBalance: 0,
          balanceType: 'Cr',
          isDefault: false
        });
        salaryPayableLedger = await db.ledgers.get(id as number);
      }

      let totalNetSalary = 0;
      let totalGrossSalary = 0;
      let totalDeductions = 0;

      const allComponents = await db.salaryComponents.where('companyId').equals(activeCompany.id!).toArray();
      
      const allLoans = await db.employeeLoans.where('companyId').equals(activeCompany.id!).and(l => l.status === 'Active').toArray();
      const allLeaves = await db.employeeLeaves.where('companyId').equals(activeCompany.id!).and(l => l.status === 'Approved').toArray();
      const allAttendance = await db.employeeAttendance.where('companyId').equals(activeCompany.id!).toArray();

      const payrollEntries = [];
      const loansToUpdate: { id: number; monthsPaid: number; status: 'Active' | 'Closed' }[] = [];

      for (const emp of employees) {
        let gross = 0;
        let deductionsTotal = 0;
        let basic = emp.basicSalary || (emp.salaryStructure?.basic || 0);
        gross += basic;

        const earnings: { name: string; amount: number }[] = [];
        const deductions: { name: string; amount: number }[] = [];

        if (emp.salaryComponents && emp.salaryComponents.length > 0) {
           for (const sc of emp.salaryComponents) {
             const def = allComponents.find(c => c.id === sc.componentId);
             if (!def) continue;
             let amount = 0;
             if (def.valueType === 'Percentage') {
               amount = (basic * (def.percentageValue || 0)) / 100;
             } else {
               amount = sc.customAmount || 0;
             }

             if (def.type === 'Allowance') {
               gross += amount;
               earnings.push({ name: def.name, amount });
             } else {
               deductionsTotal += amount;
               deductions.push({ name: def.name, amount });
             }
           }
        }
        
        // Loan EMI Deduction
        const activeLoansForEmp = allLoans.filter(l => l.employeeId === emp.id);
        for (const activeLoan of activeLoansForEmp) {
          const loanStart = new Date(activeLoan.startDate);
          // Check if loan deduction should start
          if (loanStart.getFullYear() < selectedYear || (loanStart.getFullYear() === selectedYear && (loanStart.getMonth() + 1) <= selectedMonth)) {
             const emi = activeLoan.emiAmount;
             deductionsTotal += emi;
             deductions.push({ name: `Loan EMI Deduction (#${activeLoan.id})`, amount: emi });
             
             const newMonthsPaid = activeLoan.monthsPaid + 1;
             loansToUpdate.push({
               id: activeLoan.id!,
               monthsPaid: newMonthsPaid,
               status: newMonthsPaid >= activeLoan.totalMonths ? 'Closed' : 'Active'
             });
          }
        }
        
        // Leave Without Pay Deduction
        const empLeaves = allLeaves.filter(l => l.employeeId === emp.id && l.type === 'Unpaid');
        let lwpDays = 0;
        for (const leave of empLeaves) {
           const start = new Date(leave.startDate);
           const end = new Date(leave.endDate);
           if ((start.getFullYear() === selectedYear && start.getMonth() + 1 === selectedMonth) ||
               (end.getFullYear() === selectedYear && end.getMonth() + 1 === selectedMonth)) {
                
                // Simple calculation (assuming all leave is in this month for simplicity)
                const diffTime = Math.abs(end.getTime() - start.getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                lwpDays += diffDays;
           }
        }
        
        // Check attendance data for absents and half days
        const empAttendance = allAttendance.filter(a => {
           if (a.employeeId !== emp.id) return false;
           // Format: YYYY-MM-DD
           const parts = a.date.split('-');
           if (parts.length === 3) {
             return parseInt(parts[0]) === selectedYear && parseInt(parts[1]) === selectedMonth;
           }
           return false;
        });

        for (const att of empAttendance) {
          if (att.status === 'Absent') lwpDays += 1;
          if (att.status === 'Half Day') lwpDays += 0.5;
        }
        
        if (lwpDays > 0) {
           const perDayBasic = basic / 30; // Assuming standard 30 days calculation
           const lwpDeduction = lwpDays * perDayBasic;
           deductionsTotal += lwpDeduction;
           deductions.push({ name: `LWP & Absent Deduction (${lwpDays} days)`, amount: lwpDeduction });
        } else if (emp.salaryStructure) {
           // Fallback to legacy
           const s = emp.salaryStructure;
           gross = s.basic + s.hra + s.conveyance + s.otherAllowances;
           const pfAmount = (s.basic * (s.pfDeduction || 0)) / 100;
           const esiAmount = (s.basic * (s.esiDeduction || 0)) / 100;
           const tdsAmount = (s.basic * (s.tdsDeduction || 0)) / 100;
           deductionsTotal = pfAmount + esiAmount + tdsAmount;
           
           if (s.hra > 0) earnings.push({ name: 'HRA', amount: s.hra });
           if (s.conveyance > 0) earnings.push({ name: 'Conveyance', amount: s.conveyance });
           if (s.otherAllowances > 0) earnings.push({ name: 'Other Allowances', amount: s.otherAllowances });
           
           if (pfAmount > 0) deductions.push({ name: 'PF', amount: pfAmount });
           if (esiAmount > 0) deductions.push({ name: 'ESI', amount: esiAmount });
           if (tdsAmount > 0) deductions.push({ name: 'TDS', amount: tdsAmount });
        }
        
        const net = gross - deductionsTotal;

        totalGrossSalary += gross;
        totalDeductions += deductionsTotal;
        totalNetSalary += net;

        payrollEntries.push({
          companyId: activeCompany.id!,
          employeeId: emp.id!,
          month: selectedMonth,
          year: selectedYear,
          basicSalary: basic,
          earnings,
          deductions,
          netSalary: net,
          status: 'Processed' as const,
          processedDate: systemDate
        });
        
        setProcessingLog(prev => [...prev, `Calculated for: ${emp.name} (Net: ₹${net.toFixed(2)})`]);
      }

      // Create Voucher via AccountingEngine
      const voucherEntries = [];

      // Debit Salary Expense (Gross)
      voucherEntries.push({
        ledgerId: salaryExpenseLedger!.id!,
        type: 'Dr' as const,
        amount: totalGrossSalary
      });

      // Credit Salary Payable (Net)
      voucherEntries.push({
        ledgerId: salaryPayableLedger!.id!,
        type: 'Cr' as const,
        amount: totalNetSalary
      });

      // Credit Deductions
      if (totalDeductions > 0) {
        let deductionsLedger = await db.ledgers.where('companyId').equals(activeCompany.id!).and(l => l.name === 'Payroll Deductions Payable').first();
        if (!deductionsLedger) {
           const liabilityGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Current Liabilities').first();
           const id = await db.ledgers.add({
             companyId: activeCompany.id!,
             name: 'Payroll Deductions Payable',
             groupId: liabilityGroup?.id || null,
             openingBalance: 0,
             balanceType: 'Cr',
             isDefault: false
           });
           deductionsLedger = await db.ledgers.get(id as number);
        }
        voucherEntries.push({
          ledgerId: deductionsLedger!.id!,
          type: 'Cr' as const,
          amount: totalDeductions
        });
      }

      const postData = {
         companyId: activeCompany.id!,
         type: 'Journal',
         date: systemDate,
         narration: `Payroll processed for ${selectedMonth}/${selectedYear} for ${employees.length} employees.`
      };

      const voucherId = await AccountingEngine.postVoucher(postData, voucherEntries);

      // Save Payroll Records
      for (const entry of payrollEntries) {
        await db.payroll.add({ ...entry, voucherId: voucherId as number });
      }

      for (const loanUpdate of loansToUpdate) {
         await db.employeeLoans.update(loanUpdate.id, {
            monthsPaid: loanUpdate.monthsPaid,
            status: loanUpdate.status
         });
         setProcessingLog(prev => [...prev, `Updated loan #${loanUpdate.id}: ${loanUpdate.monthsPaid} months paid. Status is now ${loanUpdate.status}.`]);
      }

      setProcessingLog(prev => [...prev, `Success! Journal Entry created (Vch No: PR/...)`, `Payroll locked for ${selectedMonth}/${selectedYear}`]);
      setTimeout(() => {
        setIsProcessing(false);
      }, 2000);

    } catch (error) {
      setProcessingLog(prev => [...prev, `Error: ${error instanceof Error ? error.message : 'Unknown error'}`]);
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[13px] text-gray-800">
      <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px]">
        <span className="w-1/3 text-left">Salary Processing</span>
        <span className="w-1/3 text-center uppercase">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center bg-[#f1f5f9] p-8">
        <div className="bg-white border-2 border-[#1e8585] shadow-xl w-[500px] flex flex-col">
          <div className="bg-[#1e8585] text-white px-4 py-2 font-bold">Process Monthly Payroll</div>
          
          <div className="p-6 flex flex-col gap-6">
            <div className="flex items-center justify-between">
               <span className="font-bold">Select Month & Year</span>
               <div className="flex gap-2">
                 <select 
                   value={selectedMonth ?? (parsedDate.getMonth() + 1)} 
                   onChange={(e) => setSelectedMonth(Number(e.target.value))}
                   className="border border-gray-300 px-2 py-1 outline-none font-bold"
                   disabled={isProcessing}
                 >
                   {Array.from({ length: 12 }, (_, i) => (
                     <option key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</option>
                   ))}
                 </select>
                 <input 
                   type="number" 
                   value={selectedYear} 
                   onChange={(e) => setSelectedYear(Number(e.target.value))}
                   className="border border-gray-300 px-2 py-1 w-[80px] outline-none font-bold"
                   disabled={isProcessing}
                 />
               </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-3 rounded text-[12px]">
               <div className="flex justify-between mb-1">
                 <span>Active Employees:</span>
                 <span className="font-bold">{employees?.length || 0}</span>
               </div>
               <div className="flex justify-between">
                 <span>Status:</span>
                 <span className={`font-bold ${isAlreadyProcessed ? 'text-red-600' : 'text-green-600'}`}>
                   {isAlreadyProcessed ? 'ALREADY PROCESSED (LOCKED)' : 'READY TO PROCESS'}
                 </span>
               </div>
            </div>

            {isProcessing ? (
              <div className="flex flex-col gap-2">
                <div className="h-2 w-full bg-gray-200 rounded overflow-hidden">
                  <div className="h-full bg-[#1e8585] animate-pulse w-full"></div>
                </div>
                <div className="bg-black text-green-400 font-mono text-[10px] p-2 h-[100px] overflow-y-auto rounded">
                   {processingLog.map((log, i) => <div key={i}>{log}</div>)}
                </div>
              </div>
            ) : (
              <button 
                onClick={handleProcessPayroll}
                disabled={isAlreadyProcessed || !employees || employees.length === 0}
                className={`py-3 rounded font-bold text-white shadow-md transition-all ${isAlreadyProcessed || !employees || employees.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#1e8585] hover:bg-[#156666] active:scale-95'}`}
              >
                {isAlreadyProcessed ? 'Payroll Locked' : 'Process & Generate Journal Entries'}
              </button>
            )}

            <div className="text-[11px] text-gray-500 italic text-center">
              Processing will auto-calculate earnings/deductions and create accounting vouchers.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
